import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import {
  createFromSnapshot,
  SCHEMA_CATEGORIES,
  SCHEMA_SENSITIVITIES,
  SCHEMA_REGULATORY_FLAGS,
} from './schemaLibraryApi';
import { INK } from 'Utils/styleTokens';

// Promote a profiled database's latest snapshot into a custom schema-library fingerprint.
// `summary` is the data-source metadata summary (carries snapshotId + tables[]); the
// dialog collects the product metadata and an optional key-table selection, then calls
// createFromSnapshot with the worker snapshot UUID.

// Auto-suggest key tables: the widest tables (most columns) are the most identifying and
// least collision-prone. Pre-check the top N so a curator can accept/trim rather than
// hunt through hundreds of rows. Kept small — the matcher weights key tables heavily.
const KEY_TABLE_SUGGEST_COUNT = 6;
const KEY_TABLE_CAP = 10;

const AddToSchemaLibraryDialog = ({ show, onHide, summary, sourceName, onSuccess }) => {
  const [vendorName, setVendorName] = useState('');
  const [productName, setProductName] = useState('');
  const [productVersion, setProductVersion] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('CUSTOM');
  const [sensitivity, setSensitivity] = useState('MEDIUM');
  const [flags, setFlags] = useState(() => new Set());
  const [keyTables, setKeyTables] = useState(() => new Set());
  const [keyTableQuery, setKeyTableQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const snapshotId = summary && (summary.snapshotId || summary.latestSnapshotId);
  const tableNames = useMemo(() => {
    const rows = (summary && summary.tables) || [];
    return rows
      .map((t) => ({ name: t.tableName, columns: t.columnCount || 0 }))
      .filter((t) => t.name);
  }, [summary]);

  // Reset per open, and seed the product name + suggested key tables.
  useEffect(() => {
    if (!show) return;
    setVendorName('');
    setProductName(sourceName || '');
    setProductVersion('');
    setDescription('');
    setCategory('CUSTOM');
    setSensitivity('MEDIUM');
    setFlags(new Set());
    setError(null);
    setKeyTableQuery('');
    const suggested = [...tableNames]
      .sort((a, b) => b.columns - a.columns)
      .slice(0, KEY_TABLE_SUGGEST_COUNT)
      .map((t) => t.name);
    setKeyTables(new Set(suggested));
  }, [show, sourceName, tableNames]);

  const filteredTables = useMemo(() => {
    const q = keyTableQuery.trim().toLowerCase();
    const rows = q ? tableNames.filter((t) => t.name.toLowerCase().includes(q)) : tableNames;
    return [...rows].sort((a, b) => a.name.localeCompare(b.name));
  }, [tableNames, keyTableQuery]);

  const toggleFlag = (flag) => {
    setFlags((prev) => {
      const next = new Set(prev);
      if (next.has(flag)) next.delete(flag);
      else next.add(flag);
      return next;
    });
  };

  const toggleKeyTable = (name) => {
    setKeyTables((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else if (next.size < KEY_TABLE_CAP) next.add(name);
      return next;
    });
  };

  const canSubmit = vendorName.trim() && productName.trim() && snapshotId && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await createFromSnapshot({
        vendorName: vendorName.trim(),
        productName: productName.trim(),
        productVersion: productVersion.trim(),
        description: description.trim(),
        category,
        defaultSensitivity: sensitivity,
        regulatoryFlags: [...flags],
        seedFromSnapshotId: snapshotId,
        keyTableNames: [...keyTables],
      });
      if (onSuccess) onSuccess(created);
      onHide();
    } catch (e) {
      setError((e && e.message) || 'Failed to add the fingerprint.');
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = { fontSize: 11, fontWeight: 600, color: INK[400], textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <Modal show={show} onHide={onHide} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Add to schema library</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!snapshotId ? (
          <Alert variant='warning'>
            This data source has no profiled snapshot yet. Run a profile first, then add it
            to the library.
          </Alert>
        ) : (
          <>
            <p style={{ fontSize: 13, color: INK[500] }}>
              Creates a fingerprint from this source's {tableNames.length} profiled tables.
              Future profile runs that match these table signatures will be identified as this
              application.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Form.Group>
                <Form.Label style={labelStyle}>Vendor *</Form.Label>
                <Form.Control
                  data-testid='lib-vendor'
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  placeholder='e.g. Acme Corp'
                />
              </Form.Group>
              <Form.Group>
                <Form.Label style={labelStyle}>Product *</Form.Label>
                <Form.Control
                  data-testid='lib-product'
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder='e.g. WidgetCRM'
                />
              </Form.Group>
              <Form.Group>
                <Form.Label style={labelStyle}>Version</Form.Label>
                <Form.Control
                  value={productVersion}
                  onChange={(e) => setProductVersion(e.target.value)}
                  placeholder='optional'
                />
              </Form.Group>
              <Form.Group>
                <Form.Label style={labelStyle}>Category</Form.Label>
                <Form.Control as='select' value={category} onChange={(e) => setCategory(e.target.value)}>
                  {SCHEMA_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </Form.Control>
              </Form.Group>
              <Form.Group>
                <Form.Label style={labelStyle}>Default sensitivity</Form.Label>
                <Form.Control as='select' value={sensitivity} onChange={(e) => setSensitivity(e.target.value)}>
                  {SCHEMA_SENSITIVITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Form.Control>
              </Form.Group>
              <Form.Group>
                <Form.Label style={labelStyle}>Description</Form.Label>
                <Form.Control
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder='optional'
                />
              </Form.Group>
            </div>

            <Form.Group>
              <Form.Label style={labelStyle}>Regulatory flags</Form.Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {SCHEMA_REGULATORY_FLAGS.map((flag) => (
                  <Form.Check
                    key={flag}
                    type='checkbox'
                    id={`flag-${flag}`}
                    label={flag}
                    checked={flags.has(flag)}
                    onChange={() => toggleFlag(flag)}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group>
              <Form.Label style={labelStyle}>
                Key tables ({keyTables.size}/{KEY_TABLE_CAP})
              </Form.Label>
              <div style={{ fontSize: 12, color: INK[500], marginBottom: 6 }}>
                Highly identifying tables (weighted 70% in matching). We've pre-selected the
                widest tables; distinctive, always-present, PII-bearing tables work best.
                Optional — leave empty to weight all tables equally.
              </div>
              <input
                type='text'
                placeholder='Filter tables…'
                value={keyTableQuery}
                onChange={(e) => setKeyTableQuery(e.target.value)}
                style={{
                  border: `1px solid ${INK[200]}`, borderRadius: 6, fontSize: 12,
                  padding: '3px 8px', marginBottom: 6, width: '100%', outline: 'none',
                }}
              />
              <div style={{ maxHeight: 180, overflow: 'auto', border: `1px solid ${INK[150]}`, borderRadius: 6, padding: 8 }}>
                {filteredTables.map((t) => {
                  const checked = keyTables.has(t.name);
                  const disabled = !checked && keyTables.size >= KEY_TABLE_CAP;
                  return (
                    <Form.Check
                      key={t.name}
                      type='checkbox'
                      id={`kt-${t.name}`}
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggleKeyTable(t.name)}
                      label={
                        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {t.name}
                          <span style={{ color: INK[400] }}> · {t.columns} cols</span>
                        </span>
                      }
                    />
                  );
                })}
                {filteredTables.length === 0 && (
                  <div style={{ fontSize: 12, color: INK[400] }}>No tables match the filter.</div>
                )}
              </div>
            </Form.Group>

            {error && <Alert variant='danger' className='mt-2'>{error}</Alert>}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide} disabled={submitting}>Cancel</Button>
        <Button
          variant='primary'
          data-testid='lib-submit'
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitting ? (<><Spinner animation='border' size='sm' className='mr-2' />Adding…</>) : 'Add fingerprint'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddToSchemaLibraryDialog;
